import { useState, useEffect, useCallback } from 'react';
import { getAllStories, StorySummary } from '../services/api.ts';

interface UseStoriesReturn {
  stories: StorySummary[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Global stories cache to share across components
const storiesCache = {
  data: null as StorySummary[] | null,
  timestamp: 0,
  subscribers: new Set<() => void>()
};

const CACHE_DURATION = 5000; // 5 second cache for stories

export const useStories = (): UseStoriesReturn => {
  const [stories, setStories] = useState<StorySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const notifySubscribers = useCallback(() => {
    storiesCache.subscribers.forEach(callback => callback());
  }, []);

  const fetchStories = useCallback(async (force = false) => {
    const now = Date.now();

    // Use cache if available and not expired
    if (!force && storiesCache.data && (now - storiesCache.timestamp) < CACHE_DURATION) {
      setStories(storiesCache.data);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const storiesData = await getAllStories();
      setStories(storiesData);

      // Update cache
      storiesCache.data = storiesData;
      storiesCache.timestamp = now;

      // Notify all subscribers
      notifySubscribers();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching stories:', err);
    } finally {
      setLoading(false);
    }
  }, [notifySubscribers]);

  const refetch = useCallback(() => {
    fetchStories(true);
  }, [fetchStories]);

  // Subscribe to cache updates
  useEffect(() => {
    const updateFromCache = () => {
      if (storiesCache.data) {
        setStories(storiesCache.data);
      }
    };

    // Add to subscribers
    storiesCache.subscribers.add(updateFromCache);
    
    // Get existing cache if available
    if (storiesCache.data) {
      setStories(storiesCache.data);
    } else {
      // Fetch if no cache
      fetchStories();
    }

    return () => {
      storiesCache.subscribers.delete(updateFromCache);
    };
  }, [fetchStories]);

  // No automatic polling - stories will be refreshed when current story status changes
  // This is handled by the progress bar's adaptive polling system

  return {
    stories,
    loading,
    error,
    refetch
  };
};
